// TelcoTrack — HR nástupy: firmy, provozní města, pojišťovny, Drive šablony (n8n).

/** Identifikátory zaměstnavatelských firem pro smlouvy. */
export const HR_COMPANY_IDS = ['protelco', 'fiber_beta', 'extermis'] as const;
export type HrCompanyId = (typeof HR_COMPANY_IDS)[number];

/** Provozní města ve smlouvách. */
export const HR_CITY_IDS = ['lovosice', 'pardubice', 'ostrava'] as const;
export type HrCityId = (typeof HR_CITY_IDS)[number];

export interface HrCompanyDef {
  id: HrCompanyId;
  /** Obchodní jméno do maker `{{FIRMA}}`. */
  name: string;
  ico: string;
  /** Sídlo / výchozí adresa firmy (`{{ADRESA_FIRMY}}`). */
  address: string;
  /**
   * Povolená provozní města (`{{MESTO}}`).
   * Jedno město = auto-vyplnění v UI; více = výběr admina.
   */
  cities: readonly HrCityId[];
}

export const HR_CITY_LABELS: Record<HrCityId, string> = {
  lovosice: 'Lovosice',
  pardubice: 'Pardubice',
  ostrava: 'Ostrava',
};

/** Lokál (6. pád) pro formulace „v Lovosicích“ / „V Lovosicích dne …“. */
export const HR_CITY_LOCATIVE: Record<HrCityId, string> = {
  lovosice: 'Lovosicích',
  pardubice: 'Pardubicích',
  ostrava: 'Ostravě',
};

/** Jednatel / podpis za zaměstnavatele — podle firmy. */
export type HrEmployerSignatory = {
  name: string;
  phone: string;
  email: string;
};

/** Výchozí (ProTelco / Fiber Beta). */
export const HR_EMPLOYER_SIGNATORY: HrEmployerSignatory = {
  name: 'Marek Smutný',
  phone: '602 450 070',
  email: 'smutny@protelco.cz',
} as const;

/** Podepisující podle zaměstnavatele. */
export const HR_EMPLOYER_SIGNATORY_BY_COMPANY: Record<HrCompanyId, HrEmployerSignatory> = {
  protelco: HR_EMPLOYER_SIGNATORY,
  fiber_beta: HR_EMPLOYER_SIGNATORY,
  extermis: {
    name: 'Viktor Smutný',
    phone: '602 450 070',
    email: 'viktor.smutny@protelco.cz',
  },
};

export function hrEmployerSignatoryForCompany(companyId: HrCompanyId): HrEmployerSignatory {
  return HR_EMPLOYER_SIGNATORY_BY_COMPANY[companyId] ?? HR_EMPLOYER_SIGNATORY;
}

export const HR_COMPANIES: readonly HrCompanyDef[] = [
  {
    id: 'protelco',
    name: 'ProTelco s.r.o.',
    ico: '06605176',
    address: 'Máchova 316/1, 410 02 Lovosice',
    cities: ['lovosice', 'pardubice', 'ostrava'],
  },
  {
    id: 'fiber_beta',
    name: 'Fiber Beta s.r.o.',
    ico: '23090553',
    address: 'Stupská 9, 198 00 Praha - Kyje',
    cities: ['lovosice'],
  },
  {
    id: 'extermis',
    name: 'Extermis s.r.o.',
    ico: '24298620',
    address: 'Máchova 316/1, 410 02 Lovosice',
    cities: ['lovosice'],
  },
] as const;

export const HR_COMPANY_BY_ID: Record<HrCompanyId, HrCompanyDef> = Object.fromEntries(
  HR_COMPANIES.map((c) => [c.id, c]),
) as Record<HrCompanyId, HrCompanyDef>;

/** UI options (firma). */
export const HR_COMPANY_OPTIONS = HR_COMPANIES.map((c) => ({
  id: c.id,
  label: c.name,
}));

/** Zdravotní pojišťovny (kód → název do `{{POJISTOVNA}}`). */
export const HR_HEALTH_INSURERS: ReadonlyArray<{ code: string; label: string }> = [
  { code: '111', label: 'VZP ČR' },
  { code: '201', label: 'VoZP' },
  { code: '205', label: 'ČPZP' },
  { code: '207', label: 'OZP' },
  { code: '209', label: 'ZPŠ' },
  { code: '211', label: 'ZPMV' },
  { code: '213', label: 'RBP' },
];

export const HR_HEALTH_INSURER_BY_CODE: Record<string, string> = Object.fromEntries(
  HR_HEALTH_INSURERS.map((i) => [i.code, i.label]),
);

/**
 * Google Drive IDs pro HR nástupy.
 * Runtime: committed Fenyvesi DOCX fixture + fill w:t → upload Google Doc.
 * `templates` je historický odkaz na živé Docs mustři (už se nekopírují).
 */
export const HR_NASTUPY_DRIVE = {
  /** Shared drive „Software“. */
  sharedDriveId: '0ADUtimVMXFhIUk9PVA',
  /** Kořenová složka pro nové nástupy. */
  parentFolderId: '1d9cJgOEirO_ONG_17AMe5m97wnuPcPCv',
  /**
   * Drive mustři (copy 1:1). HTML-seed (`fenyvesi-html-*`) se přegeneruje z DOCX.
   * Dokument bez této značky se nikdy nemaže — bere se jako živý mustr.
   */
  templates: {
    souhlasUdaje: '1pdLpm5n088D4ziyOo7NHqxIn-dIK57l-6CJyTz7ungk',
    pracovniSmlouva: '1lf495e93dXQKfZ0G_TL5l_grfv1MSGiqARz_k0rQkqg',
    mzdovyVymer: '1kpbZTCTsL2stpgQgkgo4A1BL6Qhl7m4AtcOaYLD3cOM',
    dodatek: '1EcF8dhX6syEEkyfoi_lbOy9LAbpwgc4BXON9xEoNhd0',
  },
} as const;

/** Bump = forced reseed jen HTML-seed šablon (appProperties.hrMusterVersion). */
export const HR_NASTUPY_TEMPLATE_VERSION = 'fenyvesi-docx-v1';

/** Makra, která app posílá do n8n (Google Docs replaceAll). */
export const HR_NASTUPY_MACRO_KEYS = [
  'JMENO',
  'ADRESA',
  'NASTUP',
  'MISTO_NAROZENI',
  'RODNE_CISLO',
  'CISLO_OP',
  'TELEFON',
  'EMAIL',
  'UCET',
  'DRUH_PRACE',
  'NAROZENINY',
  'POJISTOVNA',
  'MZDA',
  'MZDA_CISLO',
  'MZDA_SLOVY',
  'FIRMA',
  'ICO',
  'ADRESA_FIRMY',
  'MESTO',
  /** Lokál města — „Lovosicích“ (bez předložky). */
  'MESTO_V',
  'ZASTUPCE',
  'ZASTUPCE_TELEFON',
  'ZASTUPCE_EMAIL',
] as const;

export type HrNastupyMacroKey = (typeof HR_NASTUPY_MACRO_KEYS)[number];

export function isHrCompanyId(value: unknown): value is HrCompanyId {
  return typeof value === 'string' && (HR_COMPANY_IDS as readonly string[]).includes(value);
}

export function isHrCityId(value: unknown): value is HrCityId {
  return typeof value === 'string' && (HR_CITY_IDS as readonly string[]).includes(value);
}

/** Povolená města pro firmu; prázdné pole = neznámá firma. */
export function hrCitiesForCompany(companyId: HrCompanyId): readonly HrCityId[] {
  return HR_COMPANY_BY_ID[companyId]?.cities ?? [];
}

/** Pokud má firma jen jedno město, vrať ho; jinak null. */
export function hrDefaultCityForCompany(companyId: HrCompanyId): HrCityId | null {
  const cities = hrCitiesForCompany(companyId);
  return cities.length === 1 ? cities[0] : null;
}
